import type { GrandPasEffect } from '../../../data/full/echassier';
import { clamp } from '../../../shared/scalar';
import { ticksIntoBeat } from '../../../shared/tempo';
import { hurtEnemy, keepWhere, touches } from '../../effects';
import { applyEnemyStatus } from '../../enemy-status';
import { ownerOf } from '../../props';
import type { SkillModule } from '../../skills/types';
import type { PlayerState } from '../../state';
import type { StepContext } from '../../systems/types';

// The stilt he plants where the stride lands: `x`, `y`, until he comes down on it.
export const STILT = 'stilt';

// He strides on the dash of his movement and lands on the first beat at least `leastTicks` away.
export const grandPas: SkillModule<GrandPasEffect> = {
  cast({ state, tempo }, player, effect, _input, power) {
    const { width, height } = state.arena;
    const x = clamp(
      player.x + player.aim.x * effect.distance,
      player.radius,
      width - player.radius,
    );
    const y = clamp(
      player.y + player.aim.y * effect.distance,
      player.radius,
      height - player.radius,
    );
    const toBeat = tempo.ticksPerBeat - ticksIntoBeat(state.tick, tempo);
    const ticks = toBeat < effect.leastTicks ? toBeat + tempo.ticksPerBeat : toBeat;
    player.dash = { dx: (x - player.x) / ticks, dy: (y - player.y) / ticks, ticksLeft: ticks };
    (state.props ??= []).push({
      id: state.nextEntityId,
      kind: STILT,
      playerId: player.id,
      phase: 'flying',
      x,
      y,
      prevX: x,
      prevY: y,
      vx: player.dash.dx,
      vy: player.dash.dy,
      damage: effect.damage * power,
      // The casting tick counts down too, before the first move.
      ticksLeft: ticks + 1,
      hitsLeft: 0,
    });
    state.nextEntityId += 1;
  },

  update(ctx) {
    const { state } = ctx;
    const props = state.props;
    if (props === undefined) {
      return;
    }
    let landed = false;
    for (const prop of props) {
      if (prop.kind !== STILT) {
        continue;
      }
      prop.ticksLeft -= 1;
      const owner = ownerOf(state, prop);
      if (prop.ticksLeft <= 0 || owner.downed) {
        landed = true;
        prop.ticksLeft = 0;
        if (!owner.downed) {
          land(ctx, owner, prop.damage);
        }
      }
    }
    if (landed) {
      keepWhere(props, (prop) => prop.kind !== STILT || prop.ticksLeft > 0);
      if (props.length === 0) {
        delete state.props;
      }
    }
  },
};

function land(ctx: StepContext, owner: PlayerState, damage: number): void {
  const { state, tempo } = ctx;
  const effect = grandPasOf(ctx, owner);
  for (const enemy of state.enemies) {
    if (enemy.hp > 0 && touches(enemy, owner, effect.radius)) {
      hurtEnemy(state, enemy, damage, owner.id, owner);
      applyEnemyStatus(state, enemy, 'frozen', effect.stunBeats * tempo.ticksPerBeat);
    }
  }
}

function grandPasOf(ctx: Pick<StepContext, 'content'>, owner: PlayerState): GrandPasEffect {
  const effect = ctx.content.classes.get(owner.classId)?.skill.effect;
  if (effect?.kind !== 'grandPas') {
    throw new Error(`player ${String(owner.id)} of class "${owner.classId}" has no Grand pas`);
  }
  return effect;
}
